import posthog from 'posthog-js'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router'
import { proxy, snapshot, useSnapshot } from 'valtio'
import { logError } from '@/components/Error/Error'
import type { RadarConfig, State } from '../../../types'
import { api } from './api'
import { processRadarData } from './processor'

let initializingRadars = false
let initializingRadarData = false

// Initialize empty state with all required properties
export const state = proxy<State>({
  context: null,
  env: null,
  radarConfig: null,
  radarItems: [],
  isLoading: false,
  isAdmin: false,
  isLocal: null,
  error: null,
  availableRadars: null,
  currentRadarId: null,
  embedConfig: null as null | { apiUrl: string; radarId: string },
  userSettings: undefined, // undefined is used for loading state
  userEmail: null,
  history: null
})

export async function getRadarsConfig() {
  if (initializingRadars) {
    console.info('State', 'getRadarsConfig: Already initializing, skipping')
    return
  }
  console.info('State', 'getRadarsConfig: Starting')
  initializingRadars = true
  state.error = null

  try {
    const radars = await api.getRadarsConfig()
    console.info('State', 'getRadarsConfig: Success', {
      radarCount: radars.length
    })
    state.availableRadars = proxy(radars)
  } catch (error) {
    logError('State', 'getRadarsConfig: Error', error)
    state.error = error as Error
    if (posthog) posthog.captureException(error)
  } finally {
    initializingRadars = false
  }
}

export async function getRadarData(radarId: number) {
  if (initializingRadarData) {
    console.info('State', 'getRadarData: Already initializing, skipping', {
      radarId
    })
    return
  }
  console.info('State', 'getRadarData: Starting', { radarId })
  initializingRadarData = true
  state.error = null

  try {
    // Update radarConfig and radarData for the selected radar
    if (radarId) {
      const { availableRadars } = snapshot(state)
      const radar = availableRadars?.find(r => Number(r.id) === Number(radarId))

      if (radar) {
        console.info('State', 'getRadarData: Found radar, updating config', {
          radarName: radar.name,
          quadrantTitles: radar.config?.quadrants?.map(q => q.title),
          ringTitles: radar.config?.rings?.map(r => r.title)
        })
        state.radarConfig = proxy({ ...(radar.config as RadarConfig) })

        const { items } = await api.getRadarData(radarId)

        const processedItems = processRadarData(state.radarConfig, items)
        state.radarItems = proxy([...processedItems])
        console.info('State', 'getRadarData: Success', {
          processedItemCount: processedItems.length
        })
      } else {
        console.warn('State', 'getRadarData: Radar not found', {
          radarId,
          availableRadars
        })
      }
    } else {
      console.warn('State', 'getRadarData: No radarId provided')
    }
  } catch (error) {
    logError('State', 'getRadarData: Error', error)
    state.error = error as Error
    if (posthog) posthog.captureException(error)
  } finally {
    initializingRadarData = false
  }
}

// Create the navigation state

export function useRadarNav() {
  const { radarId } = useParams()
  const { currentRadarId, isAdmin, isLocal, history } = useSnapshot(state)
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const isHomePage = !pathname || pathname.endsWith('/')
  const isAdminRoute = pathname?.startsWith('/admin')

  const route = (newPath: string) => {
    const targetRadarId = radarId || currentRadarId || 1
    const path = `/radar/${targetRadarId}/${newPath || ''}`
    console.info('useRadarNav', 'route:', {
      newPath,
      targetRadarId,
      radarId,
      currentRadarId
    })
    if (!isLocal && history && !isAdminRoute) {
      history.push(path)
      navigate(path) // sync MemoryRouter
    } else {
      navigate(path)
    }
  }
  const routeRadar = (
    newRadarId: number,
    newPath?: string,
    search?: string
  ) => {
    // search should include the leading '?' if present
    // Strip any existing /radar/:radarId from pathname
    const strippedPath = pathname.replace(/^\/radar\/\d+\/?/, '')
    const cleanedStrippedPath = strippedPath === '/' ? '' : strippedPath
    const path = `/radar/${newRadarId}/${newPath || cleanedStrippedPath || ''}${search || ''}`
    console.info('useRadarNav', 'routeRadar:', {
      newRadarId,
      newPath,
      pathname,
      path,
      search
    })
    if (!isLocal && history && !isAdminRoute) {
      history.push(path)
      navigate(path) // sync MemoryRouter
    } else {
      navigate(path)
    }
  }

  const rootRoute = (newPath?: string) => {
    const path = `/${newPath || pathname || ''}`
    console.info('useRadarNav', 'rootRoute:', {
      newPath,
      pathname,
      path
    })
    if (!isLocal && history && !isAdminRoute) {
      history.push(path)
      navigate(path) // sync MemoryRouter
    } else {
      navigate(path)
    }
  }

  return {
    radarId: radarId || currentRadarId,
    pathname,
    route,
    routeRadar,
    rootRoute,
    isHomePage,
    isAdmin
  }
}

// Custom hook to sync search params with Forge history and MemoryRouter
export function useSyncedSearchParams() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { isLocal, history } = useSnapshot(state)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const isAdminRoute = pathname?.startsWith('/admin')

  const setSyncedSearchParams = (
    nextParams: URLSearchParams | Record<string, string>
  ) => {
    setSearchParams(nextParams)
    let search = ''
    if (nextParams instanceof URLSearchParams) {
      search = nextParams.toString() ? `?${nextParams.toString()}` : ''
    } else if (typeof nextParams === 'object') {
      const params = new URLSearchParams(nextParams)
      search = params.toString() ? `?${params.toString()}` : ''
    }
    const path = `${pathname}${search}`
    if (!isLocal && history && !isAdminRoute) {
      history.push(path)
      navigate(path) // sync MemoryRouter
    }
  }

  return [searchParams, setSyncedSearchParams] as const
}

// Expose state globally for testing purposes
if (typeof window !== 'undefined') {
  ;(window as typeof window & { techRadarState: typeof state }).techRadarState =
    state
}
